/* eslint-disable arrow-body-style */
import React, { useRef, useMemo, useReducer, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { isEqual } from 'lodash-es';
import { Dropdown, DropdownProps } from 'tea-component';
import SourceSelect from '@intelligence/components/GenreSelect/components/SourceSelect';
import CheckedValuesBox from '@intelligence/components/GenreSelect/components/CheckedValuesBox';
import CheckboxbarPanels from '@intelligence/components/GenreSelect/components/CheckboxbarPanels';
import CascadeCheckboxPanels from '@intelligence/components/GenreSelect/components/CascadeCheckboxPanels';
import useSearch from '@intelligence/components/GenreSelect/_hooks/useSearch';
import usePlacement from '@intelligence/components/GenreSelect/_hooks/usePlacement';
import { GenresReducer } from '@intelligence/components/GenreSelect/reducers';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import {
  getValidValue,
  convertCheckedGenres2Tags,
  convertCheckedGenres2TagsStr,
  convertCheckedGenres2GenresValuable,
} from '@intelligence/components/GenreSelect/utils';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import {
  GENRE_SOURCE_MAP,
  GENRE_SOURCES,
  GENRE_SOURCES_METADATA,
  SHORTCUTS,
  RELATIONABLE,
} from '@intelligence/components/GenreSelect/constants';

import type {
  IBaseProps,
  ITag,
  IShortcut,
  IGenreSelectValue,
  IGenreSource,
  IGenreSources,
  IGenreSourcesGenreOptions,
  IGenreSourcesShortcuts,
  IGenreSourcesRelationable,
  IGenreCheckboxOptionWithChildren,
  IGenreSourcesGenreCheckboxOptionsWithChildren,
} from '@intelligence/components/GenreSelect/types';
import type { IGenresReducerAction } from '@intelligence/components/GenreSelect/reducers/types';
import type { ICheckedValuesBoxInstance } from '@intelligence/components/GenreSelect/components/CheckedValuesBox';

import '@intelligence/components/GenreSelect/index.scss';

// GenreSelect品类筛选器组件的props类型
interface IGenreSelectProps extends IBaseProps {
  // 品类数据源类型与快捷勾选按钮集合（键为品类数据源类型，值为快捷勾选按钮集合）
  shortcuts?: IGenreSourcesShortcuts;
  // 品类数据源类型与relation交互配置的集合（键为品类数据源类型，值为relation交互配置）
  relationable?: IGenreSourcesRelationable;
  // 品类数据源类型集合
  sources?: IGenreSources;
  // 允许 sources 为空数组，不回落到默认 GENRE_SOURCES
  allowEmptySources?: boolean;
  // 品类数据源类型与主、副品类的Options数据集合的映射（键为品类数据源类型，值为品类Options数据集合）
  options?: IGenreSourcesGenreOptions;
  // 组件的value
  value?: IGenreSelectValue;
  // 品类勾选change回调（勾选品类时会触发，关闭品类筛选器勾选面板时不会触发）
  onChange?: (payload: IGenreSelectValue) => void;
  // 品类筛选器confirm的回调（关闭品类筛选器勾选面板时触发，勾选品类时不会触发）
  onConfirm?: (payload: IGenreSelectValue) => void;
  // 弹出层className
  overlayClassName?: string;
  // 弹出层对齐方式，参考Tea组件库 Dropdown组件的placement属性
  overlayPlacement?: DropdownProps['placement'];
  // 吸顶摘要栏上报
  filterKey?: string;
  onSummaryDisplayChange?: (key: string, display: string) => void;
}

const GenreSelect: React.FC<IGenreSelectProps> = (props) => {
  const {
    style,
    className,
    shortcuts = SHORTCUTS,
    relationable = RELATIONABLE,
    sources: sourcesProps = GENRE_SOURCES,
    allowEmptySources = false,
    options,
    value,
    onChange,
    onConfirm,
    buriedPointParams,
    overlayClassName,
    overlayPlacement,
    tipTextOutside,
    filterKey,
    onSummaryDisplayChange,
  } = props;
  // console.debug('GenreSelect props', props)
  const { locale, formatMessage } = useIntl();

  const checkedValuesBoxInstance = useRef<ICheckedValuesBoxInstance>(null);

  /**
   * 品类数据源类型列表
   * 兜底值: GENRE_SOURCES
   */
  const sources = allowEmptySources
    ? (Array.isArray(sourcesProps) ? sourcesProps : [])
    : (Array.isArray(sourcesProps) && sourcesProps.length > 0 ? sourcesProps : GENRE_SOURCES);
  /**
   * 品类数据源类型Options
   * 更新逻辑：根据sources、GENRE_SOURCES_METADATA 生成
   */
  const sourceOptions = sources.map((source) => GENRE_SOURCES_METADATA[source]);

  // 是否初始化，用于在外部传入value更新时，判断是否需要更新source、sourcesGenres，初始值为false，在完成初始化后更新为true
  const initial = useRef<boolean>(false);

  /**
   * 品类数据源类型
   * 初始值: 空字符串
   * 兜底值: 外部传入的sources[0] || 空字符串
   * 更新逻辑: 1. 初始化时，根据组件外部传入的sources、value两个数据，生成有效的初始值
   * 更新逻辑: 2. 组件外部传入value更新时，同步更新为value.source
   * 备注: 更新逻辑中1、2 都会经过getValidValue方法校验 生成有效的value
   */
  const [source, setSource] = useState<IGenreSource>('');
  // 级联模式（App Magic）保留关闭前选中的层级索引
  const [cascadeActiveIndices, setCascadeActiveIndices] = useState<number[]>([0]);
  // 非级联模式（其它数据源）保留关闭前选中的主品类索引
  const [checkboxbarActiveIndices, setCheckboxbarActiveIndices] = useState<number[]>([0]);

  // 判断当前数据源是否为级联模式（App Magic）
  const isCascadeMode = useMemo(() => {
    // 优先使用内部source状态，如果为空则使用外部value或默认值
    const currentSource = source || value?.source || sources?.[0];
    return currentSource === GENRE_SOURCE_MAP.APP_MAGIC;
  }, [source, value?.source, sources]);

  const { componentNode, placement, calcPlacementAtOpen } = usePlacement();
  const [lockedPlacement, setLockedPlacement] = useState<DropdownProps['placement'] | null>(null);
  const closePlacementTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleDropdownOpen = (): void => {
    if (closePlacementTimerRef.current) {
      clearTimeout(closePlacementTimerRef.current);
    }
    setLockedPlacement(calcPlacementAtOpen());
  };

  const handleDropdownClose = (): void => {
    handleClose();
    closePlacementTimerRef.current = setTimeout(() => {
      setLockedPlacement(null);
    }, 300);
  };

  /**
   * 品类数据源类型change回调
   * @param payload 品类数据源类型（最新的）
   */
  const handleSourceChange = (payload: IGenreSource) => {
    setSource(payload);
    handleChange(() => genres, payload);
    // 打点上传数据
    if (buriedPointParams) {
      const params = {
        ...buriedPointParams,
        extInfo: `genre/${payload}`,
        // typeId: ,
      };
      buriedPointPage(params);
    }
  };

  /**
   * 品类CheckboxOptions状态集合维护数据，品类数据源类型为键，品类CheckboxOptions为值
   * 初始值: 空对象
   * 兜底值: 空对象
   * 更新逻辑: 1. 初始化时，根据组件外部传入的sources、options、value三个数据，dispatch GenresReducer的initial action生成初始值
   * 更新逻辑: 2. 组件外部传入value更新时，dispatch GenresReducer的updateByValue action更新值
   * 备注: 更新逻辑中1、2 都会经过getValidValue方法校验 生成有效的value
   */
  const [genres, genresDispatch] = useReducer(GenresReducer, {});
  /**
   * genresDispatch的增强
   * 1、实现genresDispatch原始的功能
   * 2、执行组件onChange逻辑
   * @param action GenresReducer的Action
   */
  const genresDispatchPlus = (action: IGenresReducerAction) => {
    genresDispatch(action);
    handleChange(() => {
      return GenresReducer(genres, action);
    });
  };
  // 当前source对应的品类快捷勾选按钮
  const sourceShortcuts = useMemo<IShortcut[]>(() => shortcuts?.[source] || [], [source, shortcuts]);
  // 当前source对应的品类的relation交互配置
  const sourceRelationable = useMemo<boolean>(() => relationable?.[source] || false, [source]);
  // 当前source对应的品类CheckboxOptions列表
  const sourceGenres = useMemo<IGenreCheckboxOptionWithChildren[]>(() => genres?.[source] || [], [source, genres]);
  // 当前source对应的勾选的品类tag，用于组件Button展示（间接依赖），和弹窗中value展示
  const sourceTags = useMemo<ITag[]>(
    () => convertCheckedGenres2Tags(locale, sourceGenres, source),
    [locale, sourceGenres, source],
  );

  /**
   * 吸顶摘要栏上报辅助函数（非 useEffect，仅在特定时机手动调用）。
   * 读取当前 sourceTags 快照，生成与按钮完全一致的展示文本后上报。
   */
  const reportStickyDisplay = () => {
    if (!filterKey || !onSummaryDisplayChange) return;
    const display = sourceTags.length > 0 ? convertCheckedGenres2TagsStr(sourceTags) : '';
    onSummaryDisplayChange(filterKey, display);
  };

  const {
    isSearching,
    keywords,
    setKeywords,
    genres: sourceGenresFiltered,
    onSearch,
  } = useSearch(locale, {
    shortcuts: sourceShortcuts,
    genres: sourceGenres,
  });

  // 搜索状态下，根据需要操作的主品类获取对应的sourceGenresFiltered搜索过滤的主品类对象
  const getSourceGenresFilteredList = (genreList: string[]) => {
    const genresFilteredList = sourceGenresFiltered.filter((genreCheckboxOption) =>
      genreList?.includes(genreCheckboxOption.value),
    );
    return genresFilteredList;
  };

  /**
   * 副品类全选、取消全选（正常状态、搜索状态）
   * 正常状态下，即为全选、取消全选操作
   * 搜索状态下，即为批量勾选、批量取消勾选操作
   * @param genre 主品类value
   * @param relation 主品类relation
   * @param checked 是否全选（正常状态）、是否批量勾选（搜索状态）
   */
  const handleSubGenreAllCheckedChange = (genre: string, relation: Relation, checked: boolean) => {
    if (isSearching) {
      // 本次操作的副品类归属的主品类Checkbox的Option
      const genreCheckboxOption = sourceGenresFiltered.find((item) => item?.value === genre);
      // 本次操作的副品类归属的主品类下的副品类Checkbox的Options集合
      const subGenreCheckboxOptions = genreCheckboxOption?.children;
      if (!Array.isArray(subGenreCheckboxOptions) || !subGenreCheckboxOptions.length) return;
      /**
       * 本次操作的副品类归属的主品类下的副品类value集合
       * 如果本次操作是批量勾选，那么就过滤掉当前已勾选的副品类
       * 如果本次操作是批量取消勾选，那么就过滤掉当前未勾选的副品类
       */
      const subGenreValues = subGenreCheckboxOptions
        .filter((subGenreCheckboxOptions) => {
          return (
            subGenreCheckboxOptions?.value &&
            !subGenreCheckboxOptions?.disabled &&
            (checked ? !subGenreCheckboxOptions?.checked : subGenreCheckboxOptions?.checked)
          );
        })
        .map((subGenreCheckboxOptions) => subGenreCheckboxOptions.value);
      genresDispatchPlus({
        type: 'subGenreBatchCheckedChange',
        payload: { source, genre, relation, subGenres: subGenreValues, checked },
      });
    } else {
      genresDispatchPlus({
        type: 'subGenreAllCheckedChange',
        payload: { source, genre, relation, checked },
      });
    }
  };

  /**
   * 主品类全选、取消全选（正常状态、搜索状态）
   * 正常状态下，即为全选、取消全选操作
   * 搜索状态下，即为批量勾选、批量取消勾选操作
   * @param checked 是否全选（正常状态）、是否批量勾选（搜索状态）
   */
  const handleGenreAllCheckedChange = (checked: boolean) => {
    if (isSearching) {
      /**
       * 主品类value集合
       * 如果本次是批量勾选操作，那么就过滤掉当前已勾选的主品类
       * 如果本次是批量取消勾选操作，那么就过滤掉当前未勾选的主品类
       */
      const genreValues = sourceGenresFiltered
        .filter((genreCheckboxOption) => {
          return genreCheckboxOption?.value && (checked ? !genreCheckboxOption?.checked : genreCheckboxOption?.checked);
        })
        .map((genreCheckboxOption) => genreCheckboxOption.value);
      const genresFilteredList = getSourceGenresFilteredList(genreValues);
      genresDispatchPlus({
        type: 'genreBatchCheckedChange',
        payload: { source, genres: genreValues || [], checked, genresFilteredList },
      });
    } else {
      genresDispatchPlus({
        type: 'genreAllCheckedChange',
        payload: { source, checked },
      });
    }
  };

  /**
   * GenreSelect组件onChange逻辑
   * @param getNextGenres 获取最新GenresReducer的state的方法
   * @param nextSource 最新的品类数据源类型，默认为当前勾选的品类数据源类型
   */
  const handleChange = (
    getNextGenres: () => IGenreSourcesGenreCheckboxOptionsWithChildren,
    nextSource: IGenreSource = source,
  ) => {
    // GenreSelect组件没有配置onChange回调，直接return
    if (typeof onChange !== 'function') return;
    const nextGenres = getNextGenres();
    const nextSourceGenres = nextGenres?.[nextSource];
    const nextSourceGenresValue = convertCheckedGenres2GenresValuable(nextSourceGenres);
    onChange({ source: nextSource, genres: nextSourceGenresValue });
  };

  const handleClose = () => {
    const payload = {
      source,
      genres: convertCheckedGenres2GenresValuable(sourceGenres),
    };
    checkedValuesBoxInstance.current?.clearSearchValue();
    // 关闭组件清空重置筛选关键字
    setKeywords('');
    // 上报吸顶展示文本（与按钮完全一致）
    reportStickyDisplay();
    if (!isEqual(payload, value)) {
      onConfirm?.(payload);
    }
  };

  // 外部传入的value更新后，同步组件内部状态
  useEffect(() => {
    // 未初始化完成，不做同步value操作
    if (!initial.current) return;
    const validValue = getValidValue(sources, value);
    setSource(validValue.source);
    genresDispatch({
      type: 'update',
      payload: validValue,
    });
  }, [value]);

  // 初始化
  useEffect(() => {
    const validValue = getValidValue(sources, value);
    setSource(validValue.source);
    genresDispatch({
      type: 'initial',
      payload: {
        options,
        sources,
        value: validValue,
      },
    });
    initial.current = true;
    // 初始化完成后延迟上报吸顶展示文本（等 reducer state 稳定后 sourceTags 才准确）
    setTimeout(() => reportStickyDisplay(), 0);
  }, [options]);

  const button = useMemo<React.ReactElement>(() => {
    // 无数据
    if (!sourceTags.length) {
      return (
        <div className="dropdown-value-container">
          <span className="dropdown-value-container-text">{formatMessage({ id: `dbi_top_genre` })}</span>
          <span className="dropdown-value-placeholder">{formatMessage({ id: `dbi_select_any_genre` })} </span>
        </div>
      );
    }
    return (
      <div className="dropdown-value-container">
        <span className="dropdown-value-container-text">{formatMessage({ id: `dbi_top_genre` })}</span>
        <span className="dropdown-value-result">{convertCheckedGenres2TagsStr(sourceTags)}</span>
      </div>
    );
  }, [locale, sourceTags]);

  return (
    <div ref={componentNode} style={style} className={classnames('intelligence-genre-selector select-item', className)}>
      <Dropdown
        overlayClassName={classnames('intelligence-genre-overlay', {
          'cascade-mode': (lockedPlacement && sources.includes(GENRE_SOURCE_MAP.APP_MAGIC)) || isCascadeMode,
        }, overlayClassName)}
        size="m"
        appearance="button"
        trigger="click"
        placement={overlayPlacement ?? lockedPlacement ?? placement}
        clickClose={false}
        positionFixed={false}
        button={button}
        onOpen={handleDropdownOpen}
        onClose={handleDropdownClose}
      >
        <div>
          {/* 已勾选主、副品类集合、搜索输入框 */}
          <CheckedValuesBox
            ref={checkedValuesBoxInstance}
            tags={sourceTags}
            // 搜索
            onSearch={onSearch}
            // 删除单个已勾选主品类（同时也删除主品类下的所有已勾选副品类）
            onDeleteItem={(value: string, data?: any) => {
              // 如果 tag 中存储了路径信息（多层级品类），使用级联删除
              if (data?.path && Array.isArray(data.path)) {
                genresDispatchPlus({
                  type: 'cascadeDeleteItem',
                  payload: {
                    source,
                    path: data.path,
                  },
                });
              } else {
                // 兼容旧逻辑：删除整个主品类及其子品类
                genresDispatchPlus({
                  type: 'genreDeleteItem',
                  payload: {
                    source,
                    genre: value,
                  },
                });
              }
            }}
            // 删除全部已勾选主品类（同时也删除主品类下的所有已勾选副品类）
            onDeleteBatch={() => {
              genresDispatchPlus({
                type: 'genreDeleteBatch',
                payload: {
                  source,
                },
              });
            }}
          />
          {/* 品类数据源类型筛选器 */}
          {sourceOptions.length > 0 && (
            <SourceSelect
              options={sourceOptions}
              value={source}
              onChange={handleSourceChange}
              tipTextOutside={tipTextOutside}
            />
          )}
          {/* 主、副品类Checkbox面板 - 根据数据源动态选择组件 */}
          {sourceOptions.length > 0 && source && (source === GENRE_SOURCE_MAP.APP_MAGIC ? (
            <CascadeCheckboxPanels
              currentIndexResetFlag={keywords}
              activeIndices={cascadeActiveIndices}
              onActiveIndicesChange={setCascadeActiveIndices}
              // 品类快捷勾选按钮集合
              shortcuts={sourceShortcuts}
              // 全量主、副品类Checkbox的Options集合
              allGenres={sourceGenres}
              // 搜索后的主、副品类Checkbox的Options集合
              genres={sourceGenresFiltered}
              // App Magic 级联品类勾选回调（优先使用）
              // 始终使用 filteredGenres 参数：搜索时仅勾选匹配路径，非搜索时 sourceGenresFiltered 等于全量数据
              onCascadeCheckedChange={(path, checked) => {
                genresDispatchPlus({
                  type: 'cascadeCheckedChangeByFilter',
                  payload: { source, path, checked, filteredGenres: sourceGenresFiltered },
                });
              }}
              // App Magic 级联品类全选/取消全选回调（优先使用）
              onCascadeAllCheckedChange={(path, checked) => {
                if (isSearching && checked) {
                  // 搜索状态下 Select All：只勾选搜索过滤后可见的品类
                  // 从过滤树中根据 path 导航到目标层级，获取可见品类 values
                  let currentLevel = sourceGenresFiltered;
                  for (const pathValue of path) {
                    const matchedNode = currentLevel?.find((n) => n?.value === pathValue);
                    if (matchedNode?.children) {
                      currentLevel = matchedNode.children as IGenreCheckboxOptionWithChildren[];
                    } else {
                      currentLevel = [];
                      break;
                    }
                  }
                  const filteredValues = currentLevel.map((item) => item?.value).filter(Boolean);
                  genresDispatchPlus({
                    type: 'cascadeBatchCheckedChange',
                    payload: { source, path, checked, filteredValues, filteredGenres: sourceGenresFiltered },
                  });
                } else {
                  // 非搜索状态 或 搜索状态下 Clear All：操作该层级所有品类
                  genresDispatchPlus({
                    type: 'cascadeAllCheckedChange',
                    payload: { source, path, checked },
                  });
                }
              }}
              // 点击品类快捷勾选按钮
              onShortcutClick={(shortcut) => {
                genresDispatchPlus({
                  type: 'cascadeShortcuts',
                  payload: { source, shortcut },
                });
              }}
            />
          ) : (
            <CheckboxbarPanels
              currentIndexResetFlag={keywords}
              activeIndices={checkboxbarActiveIndices}
              onActiveIndicesChange={setCheckboxbarActiveIndices}
              // 是否展示relation筛选器
              showRelationSelectUI={sourceRelationable}
              // 品类快捷勾选按钮集合
              shortcuts={sourceShortcuts}
              // 全量主、副品类Checkbox的Options集合
              allGenres={sourceGenres}
              // 搜索后的主、副品类Checkbox的Options集合
              genres={sourceGenresFiltered}
              // 勾选、取消勾选副品类
              onSubGenreCheckedChange={(genre, relation, subGenre, checked) => {
                genresDispatchPlus({
                  type: 'subGenreCheckedChange',
                  payload: { source, genre, relation, subGenre, checked },
                });
              }}
              // 全选、取消全选（批量勾选、批量取消勾选）副品类
              onSubGenreAllCheckedChange={handleSubGenreAllCheckedChange}
              // 勾选、取消勾选主品类
              onGenreCheckedChange={(genre, relation, checked) => {
                const genresFilteredList = isSearching ? getSourceGenresFilteredList([genre]) : [];
                genresDispatchPlus({
                  type: 'genreCheckedChange',
                  payload: { source, genre, relation, checked, genresFilteredList },
                });
              }}
              // 全选、取消全选（批量勾选、批量取消勾选）主品类
              onGenreAllCheckedChange={handleGenreAllCheckedChange}
              // 勾选主品类relation
              onGenreRelationChange={(genre, relation) => {
                genresDispatchPlus({
                  type: 'genreRelationChange',
                  payload: { source, genre, relation },
                });
              }}
              // 点击品类快捷勾选按钮
              onShortcutClick={(shortcut) => {
                genresDispatchPlus({
                  type: 'genreShortcuts',
                  payload: { source, shortcut },
                });
              }}
            />
          ))}
        </div>
      </Dropdown>
    </div>
  );
};

export default GenreSelect;
